import React, { createContext, useCallback, useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import { useAuth } from './AuthContext';

export const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
  const { user, isAuthenticated, accessToken } = useAuth();
  const [activeTab, setActiveTab] = useState('Home');
  const [studentView, setStudentView] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [hasOnboarded, setHasOnboarded] = useState(true);
  const [creds, setCreds] = useState(null);
  const [onboardingInfo, setOnboardingInfo] = useState({
    interests: [],
    'job type': '',
    'share applications': '',
    referral: {
      company: '',
      'help offered': '',
      restrictions: '',
      interests: [],
    },
  });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isAchievementModalVisible, setIsAchievementModalVisible] = useState(false);

  useEffect(() => {
    // Log the user's login
    const updateLastLogin = async () => {
      if (isAuthenticated && userProfile && accessToken) {
        console.log('Updating last login');
        try {
          await axios.put(`/api/v1/user/${userProfile.user_id}/last-login`);
        } catch (error) {
          console.error('Error updating last login:', error);
        }
      }
    };

    updateLastLogin();
    console.log('Last login update function over');
  }, [isAuthenticated, userProfile, accessToken]);

  // Effect to handle hasOnboarded changes
  useEffect(() => {
    // Only update if we have userProfile data and it has the hasOnboarded field
    if (userProfile?.has_onboarded !== undefined) {
      setHasOnboarded(userProfile?.has_onboarded);
    }
  }, [userProfile]); // Only depend on userProfile changes

  // Effect to fetch user info on mount and when auth state changes
  useEffect(() => {
    if (isAuthenticated && accessToken) {
      getUserInfo();
    }
  }, [isAuthenticated, accessToken]); // Run when isAuthenticated changes

  useEffect(() => {
    const savedOnboardingInfo = localStorage.getItem('onboardingInfo');
    if (savedOnboardingInfo) {
      try {
        setOnboardingInfo(JSON.parse(savedOnboardingInfo));
      } catch (error) {
        console.error('Error parsing onboarding info from localStorage:', error);
      }
    }
  }, []);

  async function getUserInfo() {
    try {
      if (!user?.sub) {
        console.error('No user ID available');
        return;
      }

      const response = await axios.get(`/api/v1/user/${user.sub}`);
      const userData = response.data.user;

      if (!userData) {
        console.error('No user data received');
        return;
      }

      // Set the user profile first
      setUserProfile(userData);
      console.log(userData);
      setHasOnboarded(userData.has_onboarded);
    } catch (error) {
      console.error('Error in getUserInfo:', error);
    }
  }
  // Utility function to update tutorial flags
  async function updateTutorialFlag(flagName, flagValue) {
    try {
      if (!user?.sub) {
        console.error('No user ID available');
        return;
      }

      await axios.put(`/api/v1/tutorial/${user.sub}/${flagName}`, {
        flag_value: flagValue,
      });
      await getUserInfo();
    } catch (error) {
      console.error('Error updating tutorial flag:', error);
    }
  }

  // Progress tree state
  const nodes = {
    start: {
      id: 'start',
      title: 'Learn about InternHub',
      variant: 'success',
      path: '/lesson/start',
      children: ['roles'],
      x: 2,
      y: 0,
    },
    roles: {
      id: 'roles',
      title: 'Roles & Positions',
      path: '/lesson/roles',
      children: ['process'],
      x: 2,
      y: 1,
    },
    process: {
      id: 'process',
      title: 'Application Process',
      path: '/lesson/process',
      children: ['applying', 'linkedin', 'resume'],
      x: 2,
      y: 2,
    },
    applying: {
      id: 'applying',
      title: 'The Perfect Application',
      path: '/lesson/applying',
      children: ['interviews', 'alternative'],
      x: 2,
      y: 3,
    },
    resume: {
      id: 'resume',
      title: 'Resume',
      path: '/lesson/resume',
      children: ['ocs'],
      y: 3,
      x: 4,
    },
    ocs: {
      id: 'ocs',
      title: 'Career Center',
      path: '/lesson/ocs',
      y: 4,
      x: 4,
    },
    linkedin: {
      id: 'linkedin',
      title: 'LinkedIn',
      path: '/lesson/linkedin',
      y: 3,
      x: 3,
    },
    interviews: {
      id: 'interviews',
      title: 'Getting Interviews',
      path: '/lesson/interviews',
      children: ['phone', 'technical', 'behavioral'],
      x: 2,
      y: 4,
    },
    phone: {
      id: 'phone',
      title: 'Phone Screens',
      path: '/lesson/phone',
      y: 5,
      x: 1,
    },
    technical: {
      id: 'technical',
      title: 'Technical Interviews',
      path: '/lesson/technical',
      children: ['leetcode'],
      y: 5,
      x: 2,
    },
    leetcode: {
      id: 'leetcode',
      title: 'Leetcode',
      path: '/lesson/leetcode',
      y: 6,
      x: 2,
    },
    behavioral: {
      id: 'behavioral',
      title: 'Behavioral',
      path: '/lesson/behavioral',
      y: 5,
      x: 3,
    },
    alternative: {
      id: 'alternative',
      title: 'Alternative Pathways',
      path: '/lesson/alternative',
      children: ['networking'],
      x: 0,
      y: 4,
      variant: 'yellow',
    },
    clubs: {
      id: 'clubs',
      title: 'Clubs',
      path: '/lesson/clubs',
      y: 7,
      x: 1,
      variant: 'yellow',
    },
    conference: {
      id: 'conference',
      title: 'Conferences & Career Fairs',
      path: '/lesson/conference',
      y: 7,
      x: 0,
      variant: 'yellow',
    },
    networking: {
      id: 'networking',
      title: 'Networking',
      path: '/lesson/networking',
      children: ['conference', 'clubs'],
      y: 5,
      x: 0,
      variant: 'yellow',
    },
  };

  // List of completed nodes (TODO move to context)
  const [completedNodes, setCompletedNodes] = useState(null); // Start as null to indicate not loaded
  const [completedNodesLoading, setCompletedNodesLoading] = useState(true); // Track loading state
  const fetchCompletedNodes = async () => {
    console.log('GlobalContext: Fetching completed nodes for user', userProfile.user_id);
    setCompletedNodesLoading(true);
    try {
      const response = await axios.get(`/api/v1/user/${userProfile.user_id}/completed-lessons`);
      console.log(
        'GlobalContext: Successfully fetched completed nodes:',
        response.data.completed_lessons
      );
      setCompletedNodes(response.data.completed_lessons);

      // If local storage has completedNodes, then sync that with the database then delete from localstorage
      if (localStorage.getItem('completedNodes')) {
        const completedNodes = JSON.parse(localStorage.getItem('completedNodes'));
        await axios.put(`/api/v1/user/${userProfile.user_id}/completed-lessons`, {
          completed_lessons: completedNodes,
        });
        localStorage.removeItem('completedNodes');
        fetchCompletedNodes();
        return; // Exit early since we're refetching
      }
    } catch (error) {
      console.error('Error fetching completed nodes:', error);
      setCompletedNodes([]); // Set to empty array on error
    } finally {
      setCompletedNodesLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && userProfile) {
      // User is authenticated and we have their profile, fetch completed nodes
      console.log('GlobalContext: User authenticated with profile, fetching completed nodes');
      fetchCompletedNodes();
    } else if (!isAuthenticated) {
      // User is not authenticated, clear completed nodes
      console.log('GlobalContext: User not authenticated, clearing completed nodes');
      setCompletedNodes([]);
      setCompletedNodesLoading(false);
    } else {
      // If isAuthenticated is true but userProfile is null, keep loading state
      setCompletedNodesLoading(true);
    }
  }, [userProfile, isAuthenticated]);

  // Leetcode questions, here so the dailydigest can access it for stats
  const [questions, setQuestions] = useState([]);

  const saveQuestionsToLocalStorage = (questionsToSave) => {
    const solvedQuestions = questionsToSave
      .filter((q) => q.status === 'solved')
      .map((q) => q.question);
    const attemptedQuestions = questionsToSave
      .filter((q) => q.status === 'attempted')
      .map((q) => q.question);
    localStorage.setItem(
      'solvedOrAttempted',
      JSON.stringify({ solved: solvedQuestions, attempted: attemptedQuestions })
    );
  };

  // loading for leetcode questions
  const [loading, setLoading] = useState(false);
  const fetchQuestions = useCallback(async (solvedPasteContent) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/v1/leetcode-question');

      // Add status field to each question, defaulting to "unsolved"
      const questionsWithStatus = response.data.map((q) => ({
        ...q,
        status: 'unsolved',
        isOpen: false, // for dropdown
      }));

      // Retrieve solved questions from localStorage
      const solvedOrAttempted = JSON.parse(
        localStorage.getItem('solvedOrAttempted') || '{"solved": [], "attempted": []}'
      );

      // Update status of solved questions
      questionsWithStatus.forEach((q) => {
        if (solvedOrAttempted.solved.includes(q.question)) {
          q.status = 'solved';
        } else if (solvedOrAttempted.attempted.includes(q.question)) {
          q.status = 'attempted';
        }
      });

      saveQuestionsToLocalStorage(questionsWithStatus);
      setQuestions(questionsWithStatus);
      console.log('Questions fetched:', questionsWithStatus);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leetcode data:', error);
    }
  }, []);
  useEffect(() => {
    if (isAuthenticated && userProfile) {
      fetchQuestions();
    }
  }, [fetchQuestions, isAuthenticated, userProfile]);

  const [userSchool, setUserSchool] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !userProfile) return;
    const fetchUserSchool = async () => {
      try {
        const schoolResponse = await axios.get(`/api/v1/user/${userProfile.user_id}/school`);
        setUserSchool(schoolResponse.data.school);
      } catch (error) {
        console.error('Error fetching user school:', error);
      }
    };
    fetchUserSchool();
  }, [userProfile, isAuthenticated]);

  // Submit quest to the backend
  const [questPlan, setQuestPlan] = useState(null);
  const [isQuestLoading, setIsQuestLoading] = useState(true);

  // Fetch quest
  const fetchQuest = async () => {
    setIsQuestLoading(true);
    if (!userProfile) return;

    try {
      const response = await axios.get(`/api/v1/quest/user/${userProfile.user_id}`);
      if (response.data.quest) {
        setQuestPlan(response.data.quest);
        console.log('Printing quest', response.data.quest);
      } else {
        console.log('No quest found for user');
        setQuestPlan(null);
      }
    } catch (error) {
      console.error('Error fetching quest:', error);
      setQuestPlan(null);
    }
    setIsQuestLoading(false);
  };
  useEffect(() => {
    if (isAuthenticated && userProfile) {
      fetchQuest();
    }
  }, [userProfile, isAuthenticated]);

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    console.log('Showing toast message:', message);
  };

  const hideToast = () => {
    setShowToast(false);
  };

  // Trial status information
  const [trialStatus, setTrialStatus] = useState(null);
  const [trialLoading, setTrialLoading] = useState(true);
  const fetchTrialStatus = async () => {
    setTrialLoading(true);
    try {
      const response = await axios.get('/api/v1/trial/me');
      setTrialStatus(response.data);
    } catch (error) {
      console.error('Error fetching trial status:', error);
      setTrialStatus(null);
    } finally {
      setTrialLoading(false);
    }
  };
  useEffect(() => {
    if (isAuthenticated && userProfile) {
      fetchTrialStatus();
    }
  }, [userProfile, isAuthenticated]);

  return (
    <GlobalContext.Provider
      value={{
        activeTab,
        setActiveTab,
        studentView,
        setStudentView,
        userProfile,
        setUserProfile,
        hasOnboarded,
        setHasOnboarded,
        creds,
        setCreds,
        onboardingInfo,
        setOnboardingInfo,
        updateTutorialFlag,
        nodes,
        completedNodes,
        completedNodesLoading,
        setCompletedNodes,
        fetchCompletedNodes,
        questions,
        setQuestions,
        fetchQuestions,
        isQuestLoading,
        setIsQuestLoading,
        loading,
        saveQuestionsToLocalStorage,
        setLoading, // loading is for leetcode questions
        userSchool,
        setUserSchool,
        questPlan,
        setQuestPlan,
        fetchQuest,
        showToast,
        toastMessage,
        showToastMessage,
        hideToast,
        isAchievementModalVisible,
        setIsAchievementModalVisible,
        trialStatus,
        setTrialStatus,
        trialLoading,
        setTrialLoading,
        fetchTrialStatus,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
